import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bot, Loader2, Copy, Code, Eye, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AIChat() {
  const { activeSubId } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!activeSubId) return;
    api.get(`/chat/config?sub_account_id=${activeSubId}`).then(({ data }) => setCfg(data));
  }, [activeSubId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/chat/config?sub_account_id=${activeSubId}`, cfg); toast.success("Chat widget saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const iframe = `<iframe src="${BASE}/widget/chat/${activeSubId}" style="position:fixed;bottom:0;right:0;width:400px;height:640px;border:0;z-index:9999" title="Chat"></iframe>`;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="aichat-page">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Conversational AI</p>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">AI Chat Widget</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 bg-card border-border/70 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Bot className="w-5 h-5 text-primary" /><span className="font-display font-semibold">Widget settings</span></div>
            <div className="flex items-center gap-2"><Label className="text-sm">Enabled</Label>
              <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="chat-enabled-toggle" /></div>
          </div>
          <div className="space-y-1.5"><Label>Greeting message</Label>
            <Input value={cfg.greeting} onChange={(e) => setCfg({ ...cfg, greeting: e.target.value })} data-testid="chat-greeting-input" /></div>
          <div className="space-y-1.5"><Label>Business info</Label>
            <Textarea rows={3} value={cfg.business_info} onChange={(e) => setCfg({ ...cfg, business_info: e.target.value })} placeholder="Who you are, hours, location, services, contact…" data-testid="chat-business-input" /></div>
          <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-primary" />Knowledge base / FAQs</Label>
            <Textarea rows={8} value={cfg.knowledge_base} onChange={(e) => setCfg({ ...cfg, knowledge_base: e.target.value })} placeholder={"Q: Do you offer refunds?\nA: Yes, within 30 days.\n\nList your FAQs, policies and key facts here — the AI answers strictly from this."} data-testid="chat-kb-input" />
            <p className="text-xs text-muted-foreground">Powered by Claude Sonnet 4.6. The AI answers only from the info above and offers a human handoff when it's unsure.</p></div>
          <Button onClick={save} disabled={saving} className="font-semibold" data-testid="save-chat-config">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save widget"}</Button>
        </Card>

        <div className="space-y-4">
          <Card className="p-5 bg-card border-border/70 space-y-3">
            <div className="flex items-center gap-2"><Code className="w-4 h-4 text-primary" /><span className="font-display font-semibold text-sm">Embed on your site</span></div>
            <pre className="rounded-lg bg-secondary/50 border border-border/60 p-3 text-[11px] font-mono overflow-x-auto" data-testid="chat-snippet">{iframe}</pre>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-1.5 flex-1" onClick={() => { navigator.clipboard?.writeText(iframe); toast.success("Snippet copied"); }} data-testid="copy-chat-snippet"><Copy className="w-3.5 h-3.5" />Copy</Button>
              <a href={`${BASE}/widget/chat/${activeSubId}`} target="_blank" rel="noreferrer" className="flex-1"><Button size="sm" variant="outline" className="gap-1.5 w-full" data-testid="preview-chat"><Eye className="w-3.5 h-3.5" />Preview</Button></a>
            </div>
          </Card>
          <Card className="p-0 bg-card border-border/70 overflow-hidden">
            <div className="px-4 py-2 border-b border-border/60 text-xs text-muted-foreground">Live preview</div>
            <iframe key={activeSubId} src={`${BASE}/widget/chat/${activeSubId}`} title="preview" className="w-full h-[420px] border-0" data-testid="chat-preview-iframe" />
          </Card>
        </div>
      </div>
    </div>
  );
}
